import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../amazon/Amazon.css';

const API = '/api/faire/reconciliation';

// Link helpers
const NS_BASE = 'https://3646798.app.netsuite.com/app/accounting/transactions';
const nsSOLink = (id) => id ? `${NS_BASE}/salesord.nl?id=${id}` : null;
const nsJELink = (id) => id ? `${NS_BASE}/journalentry.nl?id=${id}` : null;
const faireLink = (id) => id ? `https://www.faire.com/brand-portal/orders/${id}` : null;

const TABS = [
  { key: 'monthly', label: 'Monthly Overview' },
  { key: 'detail', label: 'Month Detail' },
  { key: 'reconcile', label: 'Reconcile & Post' },
];

function fmt(cents) {
  if (cents === null || cents === undefined) return '--';
  const neg = cents < 0;
  const abs = Math.abs(cents);
  const str = `$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return neg ? `-${str}` : str;
}

function Badge({ status }) {
  const map = {
    matched: 'ok', MATCHED: 'ok',
    mismatched: 'warning', COMMISSION_MISMATCH: 'warning', AMOUNT_MISMATCH: 'warning',
    FEE_MISMATCH: 'warning', SPLIT_ORDER: 'warning',
    missing_payout: 'critical', MISSING_PAYOUT: 'critical',
    missing_order: 'critical', MISSING_ORDER: 'critical',
    missing_data: 'warning', NO_PAYOUT_DATA: 'warning',
    not_imported: 'critical', NOT_IMPORTED: 'critical',
    not_in_netsuite: 'critical', NOT_IN_NETSUITE: 'critical',
    canceled: 'pending', CANCELED: 'pending',
    return: 'pending', RETURN: 'pending', DUPLICATE: 'critical',
    draft: 'pending', reviewed: 'pending', approved: 'approved', posted: 'posted',
    error: 'critical', completed: 'ok', processing: 'warning',
    NEW: 'pending', DELIVERED: 'ok',
    imported: 'ok', failed: 'critical', pending: 'pending',
  };
  const cls = map[status] || 'pending';
  const label = (status || 'unknown').replace(/_/g, ' ');
  return <span className={`amazon-badge ${cls}`}>{label}</span>;
}

function ExternalLink({ href, children }) {
  if (!href) return <span style={{ color: '#ccc' }}>--</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#8B6F4E', textDecoration: 'none', fontWeight: 500 }}
      onMouseOver={e => e.target.style.textDecoration = 'underline'}
      onMouseOut={e => e.target.style.textDecoration = 'none'}
    >
      {children}
    </a>
  );
}

// Generate month list from Jan 2025 to current month
function generateMonths() {
  const months = [];
  const now = new Date();
  let y = 2025, m = 0; // Jan 2025
  while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth())) {
    months.push(`${y}-${String(m + 1).padStart(2, '0')}`);
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return months;
}

const ALL_MONTHS = generateMonths();

function monthLabel(monthStr) {
  const [y, m] = monthStr.split('-');
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export default function FaireReconciliation() {
  const [activeTab, setActiveTab] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(null);

  const drillToMonth = (month) => {
    setSelectedMonth(month);
    setActiveTab('detail');
  };

  return (
    <div className="amazon-page">
      <div className="amazon-page-header">
        <h1>Faire Reconciliation</h1>
        <p style={{ fontSize: '0.85rem', color: '#777', marginTop: 4 }}>
          Month-by-month reconciliation of Faire orders against NetSuite
        </p>
      </div>

      <div className="amazon-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`amazon-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'monthly' && <MonthlyOverview onDrill={drillToMonth} />}
      {activeTab === 'detail' && <MonthDetail month={selectedMonth} onChangeMonth={setSelectedMonth} />}
      {activeTab === 'reconcile' && <ReconcilePostPanel />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: Monthly Overview
// ---------------------------------------------------------------------------

function MonthlyOverview({ onDrill }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/monthly-summary`, {
      params: { startMonth: '2025-01', endMonth: ALL_MONTHS[ALL_MONTHS.length - 1] }
    })
      .then(res => setData(res.data || []))
      .catch(err => console.error('Monthly summary fetch failed:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="amazon-loading"><div className="spinner" /></div>;

  const monthMap = {};
  data.forEach(d => { monthMap[d.month] = d; });

  // Aggregates
  const totals = data.reduce((acc, d) => ({
    orders: acc.orders + (d.total_orders || 0),
    imported: acc.imported + (d.imported_count || 0),
    matched: acc.matched + (d.matched_count || 0),
    open: acc.open + (d.open_count || 0),
    revenue: acc.revenue + (d.total_revenue_cents || 0),
    payout: acc.payout + (d.total_payout_cents || 0),
    commission: acc.commission + (d.total_commission_cents || 0),
  }), { orders: 0, imported: 0, matched: 0, open: 0, revenue: 0, payout: 0, commission: 0 });

  const overallRate = totals.orders > 0 ? ((totals.matched / totals.orders) * 100).toFixed(1) : '0.0';

  return (
    <div>
      <div className="amazon-summary">
        <div className="amazon-summary-card">
          <div className="label">Total Orders</div>
          <div className="value">{totals.orders}</div>
        </div>
        <div className="amazon-summary-card">
          <div className="label">Imported</div>
          <div className="value">{totals.imported}</div>
        </div>
        <div className="amazon-summary-card">
          <div className="label">Matched</div>
          <div className="value">{totals.matched}</div>
        </div>
        <div className="amazon-summary-card">
          <div className="label">Match Rate</div>
          <div className="value" style={{ color: Number(overallRate) >= 95 ? '#27AE60' : Number(overallRate) >= 80 ? '#D4A017' : '#C0392B' }}>
            {overallRate}%
          </div>
        </div>
        <div className="amazon-summary-card">
          <div className="label">Open Items</div>
          <div className="value" style={{ color: totals.open > 0 ? '#C0392B' : '#27AE60' }}>{totals.open}</div>
        </div>
        <div className="amazon-summary-card">
          <div className="label">Revenue</div>
          <div className="value">{fmt(totals.revenue)}</div>
        </div>
        <div className="amazon-summary-card">
          <div className="label">Net Payout</div>
          <div className="value">{fmt(totals.payout)}</div>
        </div>
      </div>

      <div className="amazon-table-wrap" style={{ marginTop: '1rem' }}>
        <table className="amazon-table">
          <thead>
            <tr>
              <th>Month</th>
              <th className="num">Orders</th>
              <th className="num">Imported</th>
              <th className="num">Matched</th>
              <th className="num">Match Rate</th>
              <th className="num">Open</th>
              <th className="num">Canceled</th>
              <th className="num">Revenue</th>
              <th className="num">Commission</th>
              <th className="num">Net Payout</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {ALL_MONTHS.slice().reverse().map(month => {
              const d = monthMap[month];
              if (!d) {
                return (
                  <tr key={month} style={{ color: '#ccc' }}>
                    <td style={{ fontWeight: 500 }}>{monthLabel(month)}</td>
                    <td className="num">0</td>
                    <td className="num">0</td>
                    <td className="num">0</td>
                    <td className="num">--</td>
                    <td className="num">0</td>
                    <td className="num">0</td>
                    <td className="num">--</td>
                    <td className="num">--</td>
                    <td className="num">--</td>
                    <td></td>
                  </tr>
                );
              }
              const rate = d.total_orders > 0 ? ((d.matched_count / d.total_orders) * 100).toFixed(1) : '0.0';
              const rateColor = Number(rate) >= 95 ? '#27AE60' : Number(rate) >= 80 ? '#D4A017' : '#C0392B';
              return (
                <tr key={month} style={{ cursor: 'pointer' }} onClick={() => onDrill(month)}>
                  <td style={{ fontWeight: 500 }}>{monthLabel(month)}</td>
                  <td className="num">{d.total_orders}</td>
                  <td className="num">{d.imported_count}</td>
                  <td className="num">{d.matched_count}</td>
                  <td className="num" style={{ color: rateColor, fontWeight: 600 }}>{rate}%</td>
                  <td className="num" style={{ color: d.open_count > 0 ? '#C0392B' : '#27AE60', fontWeight: d.open_count > 0 ? 600 : 400 }}>
                    {d.open_count}
                  </td>
                  <td className="num">{d.canceled_count}</td>
                  <td className="num">{fmt(d.total_revenue_cents)}</td>
                  <td className="num">{fmt(d.total_commission_cents)}</td>
                  <td className="num">{fmt(d.total_payout_cents)}</td>
                  <td>
                    <button className="amazon-btn secondary sm" onClick={e => { e.stopPropagation(); onDrill(month); }}>
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Month Detail
// ---------------------------------------------------------------------------

function MonthDetail({ month, onChangeMonth }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const currentMonth = month || ALL_MONTHS[ALL_MONTHS.length - 1];

  // Fetch summary for selected month
  useEffect(() => {
    axios.get(`${API}/monthly-summary`, {
      params: { startMonth: currentMonth, endMonth: currentMonth }
    })
      .then(res => setSummary((res.data || [])[0] || null))
      .catch(err => console.error(err));
  }, [currentMonth]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month: currentMonth, limit: 500 };
      if (search) params.search = search;
      if (filter) params.filter = filter;
      const res = await axios.get(`${API}/orders`, { params });
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Orders fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, search, filter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  return (
    <div>
      {/* Month selector + summary */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={currentMonth}
          onChange={e => onChangeMonth(e.target.value)}
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.85rem', background: 'white', fontWeight: 500 }}
        >
          {ALL_MONTHS.slice().reverse().map(m => (
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>
        <div className="amazon-search" style={{ flex: 1, minWidth: 200 }}>
          <input
            type="text"
            placeholder="Search by order #, company, or ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.85rem', background: 'white' }}
        >
          <option value="">All Orders</option>
          <option value="open">Open / Needs Work</option>
          <option value="not_imported">Not Imported</option>
          <option value="missing_payout">Missing Payout</option>
          <option value="ns_mismatch">NS Mismatch</option>
          <option value="canceled">Canceled</option>
        </select>
      </div>

      {summary && (
        <div className="amazon-summary" style={{ marginBottom: '1rem' }}>
          <div className="amazon-summary-card">
            <div className="label">Orders</div>
            <div className="value">{summary.total_orders}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Matched</div>
            <div className="value">{summary.matched_count}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Match Rate</div>
            <div className="value" style={{
              color: summary.total_orders > 0
                ? (summary.matched_count / summary.total_orders * 100) >= 95 ? '#27AE60'
                  : (summary.matched_count / summary.total_orders * 100) >= 80 ? '#D4A017' : '#C0392B'
                : '#ccc'
            }}>
              {summary.total_orders > 0 ? ((summary.matched_count / summary.total_orders) * 100).toFixed(1) : '0.0'}%
            </div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Open</div>
            <div className="value" style={{ color: summary.open_count > 0 ? '#C0392B' : '#27AE60' }}>{summary.open_count}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Revenue</div>
            <div className="value">{fmt(summary.total_revenue_cents)}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Net Payout</div>
            <div className="value">{fmt(summary.total_payout_cents)}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="amazon-loading"><div className="spinner" /></div>
      ) : orders.length === 0 ? (
        <div className="amazon-empty">No orders found for {monthLabel(currentMonth)}</div>
      ) : (
        <div className="amazon-table-wrap">
          <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: '#888', borderBottom: '1px solid #eee' }}>
            Showing {orders.length} of {total} orders
          </div>
          <table className="amazon-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Company</th>
                <th>State</th>
                <th>Import</th>
                <th className="num">Faire Revenue</th>
                <th className="num">NS Amount</th>
                <th className="num">Variance</th>
                <th className="num">Commission</th>
                <th className="num">Rate</th>
                <th className="num">Net Payout</th>
                <th>Faire</th>
                <th>NS SO#</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const faireRevCents = o.commission_base_cents || Math.round((o.total_price || 0) * 100);
                const nsAmountCents = o.netsuite_so_queried_amount != null
                  ? Math.round(o.netsuite_so_queried_amount * 100)
                  : null;
                const varianceCents = nsAmountCents != null ? faireRevCents - nsAmountCents : null;
                const hasVariance = varianceCents != null && Math.abs(varianceCents) > 100;
                const isOpen = o.order_state !== 'CANCELED' && (
                  o.import_status !== 'imported' ||
                  o.netsuite_so_queried_amount == null ||
                  hasVariance
                );

                let rowBg;
                if (hasVariance) rowBg = '#fef5f0';
                else if (isOpen && o.order_state !== 'CANCELED') rowBg = '#fffbf0';

                return (
                  <tr key={o.faire_order_id} style={rowBg ? { background: rowBg } : undefined}>
                    <td className="sku">{o.order_number || o.faire_order_id?.slice(0, 12)}</td>
                    <td>{o.customer_company_name || '--'}</td>
                    <td><Badge status={o.order_state} /></td>
                    <td><Badge status={o.import_status} /></td>
                    <td className="num">
                      {o.commission_base_cents != null
                        ? fmt(o.commission_base_cents)
                        : `$${Number(o.total_price || 0).toFixed(2)}`}
                    </td>
                    <td className="num">
                      {nsAmountCents != null
                        ? fmt(nsAmountCents)
                        : <span style={{ color: '#ccc' }}>--</span>}
                    </td>
                    <td className="num" style={{
                      color: varianceCents == null ? '#ccc' : hasVariance ? '#C0392B' : '#27AE60',
                      fontWeight: hasVariance ? 600 : 400,
                    }}>
                      {varianceCents != null ? fmt(varianceCents) : '--'}
                    </td>
                    <td className="num">{fmt(o.commission_cents)}</td>
                    <td className="num">
                      {o.commission_bps != null
                        ? `${(o.commission_bps / 100).toFixed(0)}%`
                        : o.commission_rate
                          ? `${(Number(o.commission_rate) * 100).toFixed(0)}%`
                          : '--'}
                    </td>
                    <td className="num">{fmt(o.payout_total_cents)}</td>
                    <td>
                      <ExternalLink href={faireLink(o.faire_order_id)}>
                        {o.faire_order_id ? 'View' : '--'}
                      </ExternalLink>
                    </td>
                    <td>
                      <ExternalLink href={nsSOLink(o.netsuite_sales_order_internal_id)}>
                        {o.netsuite_sales_order_number || '--'}
                      </ExternalLink>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: Reconcile & Post
// ---------------------------------------------------------------------------

function ReconcilePostPanel() {
  const [reconciliations, setReconciliations] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedRecon, setSelectedRecon] = useState(null);
  const [reconDetail, setReconDetail] = useState(null);
  const [jePreview, setJePreview] = useState(null);
  const [approving, setApproving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/list`);
      setReconciliations(res.data || []);
    } catch (err) {
      console.error('Reconciliations fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Auto-fill dates when month changes
  useEffect(() => {
    if (selectedMonth) {
      const [y, m] = selectedMonth.split('-').map(Number);
      const start = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const end = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      setPeriodStart(start);
      setPeriodEnd(end);
    }
  }, [selectedMonth]);

  const runReconciliation = async () => {
    if (!periodStart || !periodEnd) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/run`, { periodStart, periodEnd });
      setResult(res.data);
      fetchList();
    } catch (err) {
      setResult({ error: err.response?.data?.error || err.message });
    } finally {
      setRunning(false);
    }
  };

  const viewDetail = async (reconId) => {
    if (selectedRecon === reconId) {
      setSelectedRecon(null);
      setReconDetail(null);
      setJePreview(null);
      return;
    }
    setSelectedRecon(reconId);
    setPostResult(null);
    try {
      const [detailRes, previewRes] = await Promise.all([
        axios.get(`${API}/${reconId}`),
        axios.get(`${API}/${reconId}/je-preview`),
      ]);
      setReconDetail(detailRes.data);
      setJePreview(previewRes.data);
    } catch (err) {
      console.error('Recon detail fetch failed:', err);
    }
  };

  const approveRecon = async (reconId) => {
    setApproving(true);
    try {
      await axios.post(`${API}/${reconId}/approve`);
      fetchList();
      const previewRes = await axios.get(`${API}/${reconId}/je-preview`);
      setJePreview(previewRes.data);
    } catch (err) {
      setPostResult({ error: err.response?.data?.error || err.message });
    } finally {
      setApproving(false);
    }
  };

  const postToNS = async (reconId) => {
    setPosting(true);
    try {
      const res = await axios.post(`${API}/${reconId}/post`);
      setPostResult(res.data);
      fetchList();
    } catch (err) {
      setPostResult({ error: err.response?.data?.error || err.message });
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      {/* Run controls */}
      <div style={{
        background: 'white',
        borderRadius: 8,
        padding: '1.25rem',
        marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#333', marginBottom: '0.75rem' }}>
          Run Reconciliation
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.85rem', color: '#555' }}>Month:</label>
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.85rem', background: 'white' }}
          >
            <option value="">Select month...</option>
            {ALL_MONTHS.slice().reverse().map(m => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
          </select>
          <span style={{ color: '#999', fontSize: '0.8rem' }}>or manual:</span>
          <input
            type="date"
            value={periodStart}
            onChange={e => { setPeriodStart(e.target.value); setSelectedMonth(''); }}
            style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.85rem' }}
          />
          <span style={{ color: '#999' }}>to</span>
          <input
            type="date"
            value={periodEnd}
            onChange={e => { setPeriodEnd(e.target.value); setSelectedMonth(''); }}
            style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.85rem' }}
          />
          <button
            className="amazon-btn primary"
            onClick={runReconciliation}
            disabled={running || !periodStart || !periodEnd}
          >
            {running ? 'Running...' : 'Run Reconciliation'}
          </button>
        </div>
      </div>

      {/* Run result */}
      {result && (
        <div style={{
          background: result.error ? '#fde8e8' : '#e8f5e9',
          borderRadius: 8,
          padding: '1rem 1.25rem',
          marginBottom: '1rem',
          fontSize: '0.85rem',
          color: result.error ? '#C0392B' : '#27AE60',
        }}>
          {result.error ? (
            <span>Reconciliation failed: {result.error}</span>
          ) : (
            <span>
              Reconciliation #{result.reconciliation?.id} created.{' '}
              {result.summary?.counts?.matched} matched, {result.summary?.counts?.mismatched} mismatched,{' '}
              {result.summary?.counts?.not_in_netsuite || 0} not in NS,{' '}
              {result.summary?.counts?.missing_payout} missing data, {result.summary?.counts?.missing_order} not imported.{' '}
              Variance: {fmt(result.reconciliation?.variance_cents)}.
              JE balanced: {result.je_balanced ? 'Yes' : 'No'}
            </span>
          )}
        </div>
      )}

      {/* Post result */}
      {postResult && (
        <div style={{
          background: postResult.error ? '#fde8e8' : '#e8f5e9',
          borderRadius: 8,
          padding: '1rem 1.25rem',
          marginBottom: '1rem',
          fontSize: '0.85rem',
          color: postResult.error ? '#C0392B' : '#27AE60',
        }}>
          {postResult.error ? (
            <span>Failed: {postResult.error}</span>
          ) : (
            <span>
              Journal Entry posted: {postResult.netsuite_tran_id} (ID:{' '}
              <ExternalLink href={nsJELink(postResult.netsuite_internal_id)}>
                {postResult.netsuite_internal_id}
              </ExternalLink>)
            </span>
          )}
        </div>
      )}

      {/* Reconciliation list */}
      {loading ? (
        <div className="amazon-loading"><div className="spinner" /></div>
      ) : reconciliations.length === 0 ? (
        <div className="amazon-empty">No reconciliations yet. Select a month and run one.</div>
      ) : (
        <div className="amazon-table-wrap">
          <table className="amazon-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Period</th>
                <th>Status</th>
                <th className="num">Orders</th>
                <th className="num">Matched</th>
                <th className="num">Issues</th>
                <th className="num">Faire Revenue</th>
                <th className="num">NS Revenue</th>
                <th className="num">Variance</th>
                <th>JE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reconciliations.map(r => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td>#{r.id}</td>
                    <td>{r.period_start} to {r.period_end}</td>
                    <td><Badge status={r.status} /></td>
                    <td className="num">{r.total_orders}</td>
                    <td className="num">{r.matched_count}</td>
                    <td className="num" style={{ color: r.mismatched_count > 0 ? '#C0392B' : '#27AE60' }}>
                      {(r.mismatched_count || 0) + (r.missing_payout_count || 0) + (r.missing_order_count || 0)}
                    </td>
                    <td className="num">{fmt(r.expected_revenue_cents)}</td>
                    <td className="num">{fmt(r.actual_revenue_cents)}</td>
                    <td className="num" style={{ color: r.variance_cents === 0 ? '#27AE60' : '#C0392B' }}>
                      {fmt(r.variance_cents)}
                    </td>
                    <td>
                      {r.netsuite_je_tran_id ? (
                        <ExternalLink href={nsJELink(r.netsuite_je_internal_id)}>
                          {r.netsuite_je_tran_id}
                        </ExternalLink>
                      ) : '--'}
                    </td>
                    <td>
                      <button className="amazon-btn secondary sm" onClick={() => viewDetail(r.id)}>
                        {selectedRecon === r.id ? 'Hide' : 'Details'}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded detail */}
                  {selectedRecon === r.id && reconDetail && (
                    <tr>
                      <td colSpan={11} style={{ padding: 0 }}>
                        {/* Action buttons */}
                        <div style={{
                          padding: '0.75rem 1rem',
                          background: '#fafafa',
                          borderBottom: '1px solid #eee',
                          display: 'flex',
                          gap: '0.5rem',
                          alignItems: 'center',
                        }}>
                          {r.status === 'draft' && (
                            <button className="amazon-btn secondary sm" onClick={() => approveRecon(r.id)} disabled={approving}>
                              {approving ? 'Approving...' : 'Approve'}
                            </button>
                          )}
                          {r.status === 'approved' && (
                            <button className="amazon-btn primary sm" onClick={() => postToNS(r.id)} disabled={posting}>
                              {posting ? 'Posting...' : 'Post to NetSuite'}
                            </button>
                          )}
                          {r.status === 'posted' && (
                            <span style={{ fontSize: '0.85rem', color: '#27AE60', fontWeight: 500 }}>
                              Posted to NetSuite
                              {r.netsuite_je_tran_id && (
                                <> - <ExternalLink href={nsJELink(r.netsuite_je_internal_id)}>{r.netsuite_je_tran_id}</ExternalLink></>
                              )}
                            </span>
                          )}
                        </div>

                        {/* JE Preview */}
                        {jePreview && (
                          <div style={{ padding: '0.75rem 1rem', background: '#fafafa', borderBottom: '1px solid #eee' }}>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                              Journal Entry Preview
                              <span style={{ fontWeight: 400, marginLeft: '0.5rem', color: jePreview.balanced ? '#27AE60' : '#C0392B' }}>
                                ({jePreview.balanced ? 'Balanced' : `Unbalanced: ${jePreview.difference}`})
                              </span>
                            </div>
                            <table className="amazon-table" style={{ fontSize: '0.8rem' }}>
                              <thead>
                                <tr>
                                  <th>Account</th>
                                  <th>Name</th>
                                  <th className="num">Debit</th>
                                  <th className="num">Credit</th>
                                  <th>Memo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(jePreview.lines || []).map((line, i) => (
                                  <tr key={i}>
                                    <td>{line.account || 'TBD'}</td>
                                    <td>{line.account_name}</td>
                                    <td className="num" style={{ color: line.debit > 0 ? '#333' : '#ccc' }}>
                                      {line.debit > 0 ? `$${line.debit.toFixed(2)}` : '--'}
                                    </td>
                                    <td className="num" style={{ color: line.credit > 0 ? '#333' : '#ccc' }}>
                                      {line.credit > 0 ? `$${line.credit.toFixed(2)}` : '--'}
                                    </td>
                                    <td style={{ fontSize: '0.75rem', color: '#888' }}>{line.memo}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr style={{ fontWeight: 600 }}>
                                  <td colSpan={2}>Totals</td>
                                  <td className="num">${jePreview.totalDebits?.toFixed(2)}</td>
                                  <td className="num">${jePreview.totalCredits?.toFixed(2)}</td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}

                        {/* Order details */}
                        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                          <table className="amazon-table" style={{ fontSize: '0.8rem' }}>
                            <thead>
                              <tr>
                                <th>Order #</th>
                                <th>Status</th>
                                <th>Type</th>
                                <th className="num">Faire Revenue</th>
                                <th className="num">NS Amount</th>
                                <th className="num">Variance</th>
                                <th className="num">Commission</th>
                                <th className="num">Net Payout</th>
                                <th>Faire</th>
                                <th>NS SO#</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(reconDetail.details || []).map(d => {
                                const hasVariance = d.variance_cents !== 0 && d.status !== 'canceled' && d.status !== 'missing_data' && d.status !== 'not_imported';
                                return (
                                  <tr key={d.id} style={{ background: hasVariance ? '#fef5f0' : d.status === 'matched' ? undefined : '#fef9f2' }}>
                                    <td className="sku">{d.order_number || d.faire_order_id}</td>
                                    <td><Badge status={d.status} /></td>
                                    <td><Badge status={d.discrepancy_type} /></td>
                                    <td className="num">{fmt(d.expected_revenue_cents)}</td>
                                    <td className="num">{d.netsuite_so_amount_cents != null ? fmt(d.netsuite_so_amount_cents) : '--'}</td>
                                    <td className="num" style={{
                                      color: d.variance_cents === 0 ? '#27AE60' : '#C0392B',
                                      fontWeight: d.variance_cents !== 0 ? 600 : 400,
                                    }}>
                                      {fmt(d.variance_cents)}
                                    </td>
                                    <td className="num">{fmt(d.expected_commission_cents)}</td>
                                    <td className="num">{fmt(d.expected_net_payout_cents)}</td>
                                    <td>
                                      <ExternalLink href={faireLink(d.faire_order_id)}>
                                        {d.faire_order_id ? 'View' : '--'}
                                      </ExternalLink>
                                    </td>
                                    <td>
                                      <ExternalLink href={nsSOLink(d.netsuite_so_internal_id)}>
                                        {d.netsuite_so_tran_id || '--'}
                                      </ExternalLink>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
